import './recipients_chooser.less';

import * as Contacts                    from '../contacts';
import * as Groups                      from '../groups';
import { AutoSizer, List }              from 'react-virtualized';
import { ChevronDown as DropdownArrow } from 'react-feather';
import { useCallback }                  from 'react';
import { useEffect }                    from 'react';
import { usePageContext }               from '../components/render_page';
import { useState }                     from 'react';
import classnames                       from 'classnames';
import Dropdown                         from '../components/dropdown';
import FilteredList                     from '../components/filtered_list';
import Modal                            from '../components/modal';
import React                            from 'react';
import SearchField                      from '../components/search_field';
import useBoolean                       from '../hooks/use_boolean';


export default function RecipientsChooser({ chooserIsOpen, hideChooser, record, updateRecord }) {
  const { businessID } = usePageContext();
  const readContacts   = Contacts.useReadContacts(businessID);
  const contacts       = Contacts.useEmailContacts(businessID);
  const statuses       = Contacts.useContactsStatuses(businessID);
  const group          = Groups.useGroup(record.group);

  const { succeeded, idle } = statuses.readStatus;
  const notLoaded           = (idle && !succeeded);
  const loading             = (!idle && !succeeded);

  useEffect(function readContactsIfNeeded() {
    if (chooserIsOpen && notLoaded)
      readContacts();
  }, [ chooserIsOpen, notLoaded, readContacts ]);

  useRealContactsInRecord({ contacts, updateRecord });

  function onClickDone() {
    const allInGroup = isAllInGroup({ record, group });
    if (!allInGroup)
      updateRecord({ group: null });
    hideChooser();
  }

  return (
    <Modal isOpen={chooserIsOpen} closeModal={hideChooser} className="recipients-chooser">
      <div className="title">
        <span className="title-text">Pick Contacts</span>
        <div className="link done-button" onClick={onClickDone}>Done</div>
      </div>
      {loading && <Loading/>}
      {!loading && <ChooserContent {...{ contacts, record, updateRecord }}/>}
    </Modal>
  );
}


function useRealContactsInRecord({ contacts, updateRecord }) {
  // Campaign recipients are not full-fledged contacts. They have an id, but are
  // missing other contact properties we need, like sorting. So once contacts
  // are loaded, we re-populate record.recipients with actual contacts, using
  // the ids in record.recipients to look them up. We only want to do this once,
  // so we use a boolean flag to track that.
  const [ recipientsUpdated, setRecipientsUpdated ] = useBoolean(false);

  useEffect(function updateRecordWithContacts() {
    if (!recipientsUpdated && contacts.length > 0) {
      updateRecord(prevRecord => ({
        ...prevRecord,
        recipients: getContactsByIDs(contacts, prevRecord.recipients)
      }));
      setRecipientsUpdated();
    }
  }, [ contacts, recipientsUpdated, setRecipientsUpdated, updateRecord ]);
}


function Loading() {
  return (
    <div className="loading">Loading contacts…</div>
  );
}


const fuseOptions = {
  shouldSort:         true,
  threshold:          0.3,
  location:           0,
  distance:           100,
  maxPatternLength:   72,
  minMatchCharLength: 1,
  ignoreLocation:     true,
  keys:               [
    {
      name:   'name.full',
      weight: 0.1
    },
    {
      name:   'name.given',
      weight: 0.09
    },
    {
      name:   'name.surname',
      weight: 0.08
    },
    {
      name:   'contact.email.address',
      weight: 0.07
    },
    {
      name:   'contact.phone.text',
      weight: 0.07
    }
  ]
};


function ChooserContent({ contacts, record, updateRecord }) {
  const { businessID } = usePageContext();
  const businessGroups = Groups.useGroups(businessID);

  const allContacts = {
    name:      'all',
    title:     'All Contacts',
    customers: contacts
  };

  const groups = [
    allContacts,
    ...businessGroups
  ];

  const [ query, setQuery ] = useState('');
  const group               = getSelectedGroup({ groups, record });
  const initialList         = getGroupContacts({ contacts, group });
  const [ list, setList ]   = useState(initialList);

  const { addContacts, removeContacts, toggleContact } = useRecordHelpers({ updateRecord });

  return (
    <div className="chooser-content">
      <GroupsDropdown {...{ contacts, groups, record, updateRecord, setList }}/>
      <SearchField {...{ query, setQuery }}/>
      <FilteredList
        collection={list}
        query={query}
        emptyView={<EmptyView/>}
        fuseOptions={fuseOptions}
      >
        {items => (
          <>
            <ListInfo {...{ items, record, query, addContacts, removeContacts }}/>
            <CheckboxList {...{ items, record, toggleContact }}/>
            <SelectionInfo {...{ items, contacts, group, record, setQuery, setList }}/>
          </>
        )}
      </FilteredList>
    </div>
  );
}


function useRecordHelpers({ updateRecord }) {

  const addContacts = useCallback(function addContacts(contactsToAdd) {
    const idsToAdd = contactsToAdd.map(contact => contact.id);
    updateRecord(prevRecord => ({
      ...prevRecord,
      recipients: [
        ...prevRecord.recipients.filter(contact => !idsToAdd.includes(contact.id)),
        ...contactsToAdd
      ]
    }));
  }, [ updateRecord ]);

  const removeContacts = useCallback(function removeContacts(contactsToRemove) {
    const idsToRemove = contactsToRemove.map(contact => contact.id);
    updateRecord(prevRecord => ({
      ...prevRecord,
      recipients: prevRecord.recipients.filter(contact => !idsToRemove.includes(contact.id))
    }));
  }, [ updateRecord ]);

  const toggleContact = useCallback(function toggleContact(contact) {
    updateRecord(prevRecord => {
      const isSelected = isContactSelected(prevRecord.recipients, contact);
      if (isSelected) {
        return {
          ...prevRecord,
          recipients: prevRecord.recipients.filter(item => item.id !== contact.id)
        };
      } else {
        return {
          ...prevRecord,
          recipients: [
            ...prevRecord.recipients,
            contact
          ]
        };
      }
    });
  }, [ updateRecord ]);

  return {
    addContacts,
    removeContacts,
    toggleContact
  };
}

function isContactSelected(recipients, contact) {
  return recipients.map(({ id }) => id).includes(contact.id);
}


function GroupsDropdown({ contacts, groups, record, updateRecord, setList }) {
  const selectedGroup = getSelectedGroup({ groups, record });
  return (
    <Dropdown className="groups-dropdown">
      <Dropdown.Header>
        <Group group={selectedGroup}/>
      </Dropdown.Header>
      <Dropdown.Content>
        {groups.map(group => <GroupItem key={group.name} {...{ contacts, group, record, updateRecord, setList }}/>)}
      </Dropdown.Content>
      <DropdownArrow className="dropdown-arrow"/>
    </Dropdown>
  );
}

function GroupItem({ contacts, group, record, updateRecord, setList }) {
  const { name }  = group;
  const active    = (name === record.group);
  const className = classnames({ active });

  function onClick() {
    const groupContacts = getGroupContacts({ contacts, group });
    setList(groupContacts);
    updateRecord({ group: name });
  }

  return (
    <Dropdown.Item onClick={onClick} className={className}>
      <Group {...{ group }}/>
    </Dropdown.Item>
  );
}

function Group({ group }) {
  const { title, description, customers } = group;
  return (
    <div className="group">
      <div className="group-title">
        {title}
        <span className="badge">{customers.length}</span>
      </div>
      {description && (
        <div className="group-description">
          {description}
        </div>
      )}
    </div>
  );
}


const maximumSelectionSize = 1500;

function ListInfo({ items, record, query, addContacts, removeContacts }) {
  const hasItems            = (items.length > 0);
  const numberSelected      = record.recipients.length;
  const numberUnselected    = items.filter(item => !isContactSelected(record.recipients, item)).length;
  const allItemsAreSelected = items.every(item => isContactSelected(record.recipients, item));
  const showSelectAll       = shouldShowSelectAll({ hasItems, numberSelected, numberUnselected, allItemsAreSelected });
  const showDeselectAll     = shouldShowDeselectAll({ hasItems, allItemsAreSelected });

  function onClickSelectAll() {
    addContacts(items);
  }

  function onClickDeselectAll() {
    removeContacts(items);
  }

  const matches = items.length === 1 ? 'match' : 'matches';

  return (
    <div className="list-info">
      <div className="actions">
        {showSelectAll && <span className="link select-all" onClick={onClickSelectAll}>Select All</span>}
        {showDeselectAll && <span className="link deselect-all" onClick={onClickDeselectAll}>Deselect All</span>}
      </div>
      {query && (
        <div className="query-matches">
          {items.length} {matches}
        </div>
      )}
    </div>
  );
}

function shouldShowSelectAll({ hasItems, numberSelected, numberUnselected, allItemsAreSelected }) {
  const resultingSelection = numberSelected + numberUnselected;
  const wouldBeTooMany     = resultingSelection > maximumSelectionSize;
  return (hasItems && !wouldBeTooMany && !allItemsAreSelected);
}

function shouldShowDeselectAll({ hasItems, allItemsAreSelected }) {
  return (hasItems && allItemsAreSelected);
}


function SelectionInfo({ items, contacts, group, record, setQuery, setList }) {
  const showingIDs             = items.map(item => item.id);
  const selectedIDs            = record.recipients.map(item => item.id);
  const allShowingAreSelected  = showingIDs.every(id => selectedIDs.includes(id));
  const allSelectedAreShowing  = selectedIDs.every(id => showingIDs.includes(id));
  const redundant              = (allShowingAreSelected && allSelectedAreShowing);
  const numberSelected         = record.recipients.length;
  const hasSelected            = (numberSelected > 0);
  const renderShowSelectedLink = (hasSelected && !redundant);

  const groupIDs          = group.customers.map(item => item.id);
  const groupIsAllShowing = groupIDs.every(id => showingIDs.includes(id));
  const renderShowAllLink = !groupIsAllShowing;

  function onClickShowSelected() {
    setQuery('');
    setList(record.recipients);
  }

  function onClickShowAll() {
    setQuery('');
    const groupContacts = getGroupContacts({ contacts, group });
    setList(groupContacts);
  }

  if (hasSelected || renderShowSelectedLink || renderShowAllLink) {
    return (
      <div className="selection-info">
        <div className="number-selected">
          {hasSelected && <>{numberSelected} selected</>}
        </div>
        <div className="actions">
          {renderShowSelectedLink && (
            <span className="link show-selected" onClick={onClickShowSelected}>Show Selected</span>
          )}
          {renderShowAllLink && (
            <span className="link show-all" onClick={onClickShowAll}>Show All</span>
          )}
        </div>
      </div>
    );
  } else
    return null;
}


function CheckboxList({ items, record, toggleContact }) {
  const sortedItems = items.slice().sort(byIndexLetterAndName);
  const rows        = addInitialLettersToContacts(sortedItems);

  return (
    <div className="contact-list">
      <AutoSizer>
        {({ width, height }) => (
          <List
            width={width}
            height={height}
            rowCount={rows.length}
            rowHeight={getRowHeightHandler({ rows })}
            rowRenderer={getRowRenderer({ rows, record, toggleContact })}
            scrollToAlignment="start"
          />
        )}
      </AutoSizer>
    </div>
  );
}


const contactRowHeight     = 42;
const letterRowHeight      = 44;
const firstLetterRowHeight = 24; // first letter divider doesn't have top padding

function getRowHeightHandler({ rows }) {
  return function rowHeightHandler({ index }) {
    const item      = rows[index];
    const isContact = !!item.id;
    if (isContact)
      return contactRowHeight;
    else if (index === 0)
      return firstLetterRowHeight;
    else
      return letterRowHeight;
  };
}


function getRowRenderer({ rows, record, toggleContact }) {
  return function rowRenderer({ key, index, style }) {
    const item      = rows[index];
    const isContact = !!item.id;
    if (isContact)
      return (<Contact key={key} contact={item} {...{ record, toggleContact, style }}/>);
    else
      return (<LetterDivider key={key} letter={item} {...{ style }}/>);
  };
}


function EmptyView() {
  return (
    <div className="empty-contact-list">
      No contacts match your search.<br/>Please try again.
    </div>
  );
}


function getSelectedGroup({ groups, record }) {
  return groups.find(group => group.name === record.group) || groups[0];
}


function getGroupContacts({ contacts, group }) {
  if (group.name === 'all')
    return contacts;
  else
    return getContactsByIDs(contacts, group.customers);
}

function getContactsByIDs(contacts, list) {
  const ids = list.map(contact => contact.id);
  return contacts.filter(contact => ids.includes(contact.id));
}

function byIndexLetterAndName(a, b) {
  return a.sorting.localeCompare(b.sorting);
}


function addInitialLettersToContacts(contacts) {
  return contacts.reduce((all, contact) => {
    const last           = all[all.length - 1];
    const lastLetter     = last?.sorting[1];
    const letter         = contact.sorting[1];
    const changingLetter = letter !== lastLetter;
    if (changingLetter)
      all.push(letter);

    all.push(contact);

    return all;
  }, []);
}


function Contact({ contact, record, toggleContact, style }) {
  const checked = isContactSelected(record.recipients, contact);

  function onClick() {
    toggleContact(contact);
  }

  return (
    <div className="contact" style={style} onClick={onClick}>
      <Checkbox checked={checked}/>
      {contact.name.full}
    </div>
  );
}

function Checkbox({ checked }) {
  const className = classnames('checkbox', { checked });
  return (
    <div className={className}/>
  );
}

function LetterDivider({ letter, style }) {
  const id = `letter-${letter}`;
  return (
    <div className="letter-divider" id={id} style={style}>
      <div className="letter-bar">
        {letter}
      </div>
    </div>
  );
}


function isAllInGroup({ record, group }) {
  if (group) {
    const selectedIDs = record.recipients.map(contact => contact.id);
    const groupIDs    = group.customers.map(contact => contact.id);
    return selectedIDs.every(id => groupIDs.includes(id));
  } else
    return false;

}
